import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ContractTypes } from '@app/features/contracts/contract.model';
import { Employee, PersonTitle, PersonTitles } from '@app/features/employees/employee.model';
import { ControlsOf } from '@app/shared/forms/controls-of.form-group';
import { PhoneField } from '@app/shared/forms/phone-field/phone-field';
import { SelectCountry } from '@app/shared/forms/select-country/select-country';
import { SelectNationality } from '@app/shared/forms/select-nationality/select-nationality';
import { securitySocialNumberValidator } from '@app/shared/forms/social-security.validator';
import { SsnFormatterDirective } from '@app/shared/forms/ssn-formatter.directive';
import { TitlecaseDirective } from '@app/shared/pipes/titlecase.directive';
import { ToUppercaseDirective } from '@app/shared/pipes/to-uppercase.directive';
import diff from 'microdiff';
import { map, startWith } from 'rxjs/operators';

export interface EmployeeFormValue {
  id?: string | null;
  birthName: string | null;
  firstname: string;
  lastname: string;
  email: string | null;
  phoneNumber: string | null;
  title: PersonTitle;
  birthdate: string | null;
  birthplace: string | null;
  birthCountry: string | null;
  socialSecurityNumber: string | null;
  address: string | null;
  additionalAddress: string | null;
  postalCode: string | null;
  city: string | null;
  nationality: string | null;
}

@Component({
  selector: 'app-employee-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatCheckboxModule,
    SsnFormatterDirective,
    ToUppercaseDirective,
    MatIconModule,
    SelectCountry,
    SelectNationality,
    PhoneField,
    TitlecaseDirective,
  ],
  templateUrl: './employee-form.html',
  styleUrls: ['./employee-form.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class EmployeeForm implements OnInit {
  loadUtils = () => import('intl-tel-input/utils');

  ContractTypes = ContractTypes;
  PersonTitles = PersonTitles;
  Validators = Validators;

  form = new FormGroup<ControlsOf<Employee>>({
    id: new FormControl<string | null>(null),
    firstname: new FormControl('', { nonNullable: true }),
    lastname: new FormControl('', { nonNullable: true }),
    birthName: new FormControl<string | null>(null),

    email: new FormControl<string | null>(null, { validators: [Validators.email] }),
    phoneNumber: new FormControl<string | null>(null),
    title: new FormControl<PersonTitle>('Monsieur', { nonNullable: true }),
    birthdate: new FormControl<string | null>(null),
    birthplace: new FormControl<string | null>(null),
    birthCountry: new FormControl<string | null>(null),

    address: new FormControl<string | null>(null),
    postalCode: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
    additionalAddress: new FormControl<string | null>(null),
    nationality: new FormControl<string | null>(null),
    socialSecurityNumber: new FormControl<string | null>(null, {
      validators: securitySocialNumberValidator(),
    }),
  });

  initWith = input.required<EmployeeFormValue>();
  phoneNumberValue = '';
  validation = input<boolean>(false);
  readOnly = input<boolean>(false);
  cdr = inject(ChangeDetectorRef);
  withoutSecuritySocialNumber = false;

  private initialValues?: EmployeeFormValue;
  readonly valueSig = toSignal(
    this.form.valueChanges.pipe(
      startWith(null),
      map(() => this.form.getRawValue()),
    ),
    {
      initialValue: this.form.getRawValue(),
    },
  );

  constructor() {
    effect(() => this.valueSig());
    effect(() => {
      if (this.validation()) {
        this.form.markAllAsTouched();
      }
    });
    effect(() => {
      this.applyReadOnlyState(this.readOnly());
    });
  }

  ngOnInit(): void {
    this.initialValues = structuredClone(this.initWith());
    this.phoneNumberValue = this.initWith().phoneNumber || '';
    this.form.patchValue(this.initialValues);
    this.onWithoutSocialSecurityNumberChanged(
      this.initialValues.socialSecurityNumber === '999999999999999',
    );
  }

  get isValid(): boolean {
    return this.form.valid || this.form.disabled;
  }

  get isDirty(): boolean {
    if (!this.initialValues) return false;
    const current: EmployeeFormValue = {
      ...this.form.getRawValue(),
    };
    const normalize = (obj: Record<string, unknown>) =>
      Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v]));
    const modified = diff(
      normalize(this.initialValues as unknown as Record<string, unknown>),
      normalize(current as unknown as Record<string, unknown>),
    );
    return modified.length > 0;
  }

  get hasEmployeeId(): boolean {
    return !!this.form.controls.id?.value;
  }

  get editEmployeeDisabled(): boolean {
    return this.readOnly() || this.hasEmployeeId;
  }

  private applyReadOnlyState(readOnly: boolean): void {
    if (readOnly) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
    if (this.withoutSecuritySocialNumber) {
      this.form.controls.socialSecurityNumber.disable({ emitEvent: false });
    }
  }

  onCivilityChange() {
    this.onWithoutSocialSecurityNumberChanged(this.withoutSecuritySocialNumber);
    this.cdr.detectChanges();
  }

  onWithoutSocialSecurityNumberChanged(checked: boolean) {
    this.withoutSecuritySocialNumber = checked;
    if (this.withoutSecuritySocialNumber) {
      const start = this.form.controls.title.value === 'Monsieur' ? '1' : '2';
      this.form.get('socialSecurityNumber')?.setValue(start + '99999999999999');
      this.form.controls.socialSecurityNumber.disable({ emitEvent: false });
    } else {
      if (this.form.get('socialSecurityNumber')?.value === '999999999999999') {
        this.form.get('socialSecurityNumber')?.setValue('');
      }
      this.form.controls.socialSecurityNumber.enable({ emitEvent: false });
    }

    if (this.readOnly()) {
      this.form.controls.socialSecurityNumber.disable({ emitEvent: false });
    }
  }

  updateComputedSocialSecurityNumber() {
    if (this.withoutSecuritySocialNumber) return;
    let ssn = '';
    ssn += this.form.controls.title.value === 'Monsieur' ? '1' : '2';
    if (!this.form.controls.birthdate.value) return;
    const birthdate = new Date(this.form.controls.birthdate.value);
    ssn += String(birthdate.getFullYear()).slice(-2);
    ssn += String(birthdate.getMonth() + 1).padStart(2, '0');
  }

  private restorePhoneNumber(phoneNumber: string): void {
    if (this.form.controls.phoneNumber.value !== phoneNumber) {
      this.form.controls.phoneNumber.setValue(phoneNumber, { emitEvent: false });
    }
  }
}
